import React, { useCallback, useEffect, useRef, useState } from 'react';
import { 
	AccountCircle, 
	ErrorOutline, 
	Send, 
	Add, 
	AttachMoney, 
	Description as DescriptionIcon, 
	Assignment, 
	TimerOff as TimerOffIcon,
	Image as ImageIcon,
	AttachFile as AttachFileIcon,
	Mic as MicIcon,
	Delete as DeleteIcon
} from '@material-ui/icons';
import {
	ChatHeader,
	ChatHeaderInfo,
	ChatMessages,
	Message,
	MessageContent,
	MessageTimestamp,
	ChatInputForm,
	ChatInput,
	SendButton,
	DateSeparator,
	ButtonSelectOperator,
	LoadMoreButton,
	LoadingMessageContainer,
	ChatEndedMessage,
	ActionButton,
	ActionDialog,
	ActionList,
	ActionItem,
} from './styles';
import { formatDate } from '../../utils/dateFormatters';
import { formatMessageContent } from '../../utils/messageFormatters';
import { ArrowDropDown } from '@mui/icons-material';
import Modal from 'newComponents/Modal';
import { useChatArea } from './hooks/useChatArea.hooks';
import SelectSearch from 'newComponents/SelectSearch';
import { BootstrapInput } from 'app/routes/CaseDetails/CaseCard/NegotiationTab/styles';
import { useQuery } from 'react-query';
import { useMessage } from '../../context/MessageContext';
import { ChatActions } from '../ChatActions';
import DialogComponent from 'newComponents/DialogComponent';
import { useSelector } from 'react-redux';
import { useNotifyAlert } from 'context/NotifyAlert';
import { MediaMessage } from './components/MediaMessage';

const ChatArea = ( {
	useForm,
} ) => {
	const { fetchMessages, messagesCursor, messages, handleSendMessage, closeChat, assignChat, getTemplates, sendTemplateMessage } = useMessage();
	const userEmail = useSelector( state => state.auth.userLogin );
	const { setShowNotify } = useNotifyAlert();
	const [ isLoading, setIsLoading ] = useState( false );
	const [ shouldScrollToBottom, setShouldScrollToBottom ] = useState( false );

	const [ loadingCloseService, setLoadingCloseService ] = useState( false );
	const [ loadingAssignChat, setLoadingAssignChat ] = useState( false );
	const [ openDialog, setOpenDialog ] = useState( false );
	const [ chatIdInput, setChatIdInput ] = useState( '' );
	const { isAdmin, userId, handleOpenModal, handleCloseModal, visible, handleFetchOperators, handleUpdateOperator, selectedChat } = useChatArea();
	const [ openActionDialog, setOpenActionDialog ] = useState( false );
	const actionButtonRef = useRef( null );
	const [ actionDialogPosition, setActionDialogPosition ] = useState( { top: 0, left: 0 } );
	const textareaRef = useRef( null );
	const imageInputRef = useRef( null );
	const documentInputRef = useRef( null );

	const [ selectedImage, setSelectedImage ] = useState( null );
	const [ imagePreview, setImagePreview ] = useState( null );
	const [ selectedDocument, setSelectedDocument ] = useState( null );

	const [ isRecording, setIsRecording ] = useState( false );
	const [ recordingTime, setRecordingTime ] = useState( 0 );
	const [ audioLevels, setAudioLevels ] = useState( [] );

	const barMultiplier = 25;
	const maxBarHeight = 45;

	const mediaRecorderRef = useRef( null );
	const recordingTimerRef = useRef( null );
	const analyserRef = useRef( null );
	const dataArrayRef = useRef( null );
	const animationRef = useRef( null );
	const streamRef = useRef( null );
	const audioContextRef = useRef( null );
	const chunksRef = useRef( [] );
	const isPlayingRef = useRef( false );

	const messageValue = useForm.watch( 'message' );

	// Estado para modal de imagem fullscreen
	const [ fullscreenImage, setFullscreenImage ] = useState( null );

	const resizeTextarea = () => {
		const textarea = textareaRef.current;
		if ( textarea ) {
			textarea.style.height = 'auto'; // Reset height
			textarea.style.height = `${Math.min( textarea.scrollHeight, 300 )}px`;
		}
	};

	useEffect( () => {
		resizeTextarea();
	}, [ messageValue ] );

	const handleChange = ( e ) => {
		useForm.setValue( 'message', e.target.value );
	};

	const { data: operators } = useQuery( {
		queryKey : [ 'operators' ],
		queryFn  : () => handleFetchOperators()
	} );

	const chatRef = useRef( null );
	const isInitialLoad = useRef( true );
	const isCaseAssigned = !!selectedChat?.caseId;
	const isConversationEnded = !!selectedChat?.closedAt;

	const userPermittedToAssignChat = !selectedChat?.userId ? false : selectedChat?.userEmail === userEmail ? false : !isAdmin;

	const isUserResponsible = selectedChat?.userEmail === userEmail;
	const hasCaseId = !!selectedChat?.caseId;
	const isActionButtonEnabled = isUserResponsible && hasCaseId;

	useEffect( () => {
		if ( chatRef.current && selectedChat && messages.length > 0 ) {
			setTimeout( () => {
				chatRef.current.scrollTop = chatRef.current.scrollHeight;
			}, 100 );
		}
		if ( selectedChat && selectedChat?.caseId ) {
			getTemplates( {
				caseId      : parseInt( selectedChat.caseId?.replace( /\D/g, '' ) ),
				templateIds : [ 1, 2, 3, 4 ]
			} );
		}
	}, [ selectedChat ] );

	const groupMessagesByDate = ( messages ) => {
		const groups = {};
		messages.forEach( message => {
			const date = new Date( message.createdAt ).toDateString();
			if ( !groups[date] ) {
				groups[date] = [];
			}
			groups[date].push( message );
		} );
		return groups;
	};

	const handleOpenAssignDialog = () => {
		setOpenDialog( ( prev ) => !prev );
	};

	const handleOpenActionDialog = () => {
		if ( openActionDialog ) {
			handleCloseActionDialog();
			return;
		}
		
		if ( actionButtonRef.current ) {
			const buttonRect = actionButtonRef.current.getBoundingClientRect();
			setActionDialogPosition( {
				top  : buttonRect.top + window.scrollY - 300,
				left : buttonRect.left + window.scrollX
			} );
		}
		setOpenActionDialog( true );
	};

	const handleCloseActionDialog = () => {
		setOpenActionDialog( false );
	};

	const handleLoadMessages = async () => {
		if ( !chatRef.current ) return;
		setIsLoading( true );

		const chatDiv = chatRef.current;
		const previousScrollHeight = chatDiv.scrollHeight;
		const previousScrollTop = chatDiv.scrollTop;

		await fetchMessages();

		setIsLoading( false );

		chatDiv.scrollTop = chatDiv.scrollHeight - previousScrollHeight + previousScrollTop;
	};

	const convertToBase64 = ( file ) => {
		return new Promise( ( resolve, reject ) => {
			const reader = new FileReader();
			reader.readAsDataURL( file );
			reader.onload = () => {
				const [ meta, base64String ] = reader.result.split( ',' );
				const mimetype = meta.match( /data:(.*);base64/ )[1];
				resolve( { mimetype, base64String } );
			};
			reader.onerror = ( error ) => reject( error );
		} );
	};

	const handleImageUpload = async ( event ) => {
		const file = event.target.files[0];
		if ( file ) {
			if ( !file.type.startsWith( 'image/' ) ) {
				setShowNotify( {
					status : 'error',
					text   : 'Por favor, selecione apenas arquivos de imagem.'
				} );
				return;
			}

			try {
				const { mimetype, base64String } = await convertToBase64( file );
				setSelectedImage( { base64String, mimetype } );
				setImagePreview( URL.createObjectURL( file ) );
				handleCloseActionDialog();
			} catch ( error ) {
				console.error( 'Erro ao converter imagem:', error );
				setShowNotify( {
					status : 'error',
					text   : 'Erro ao processar a imagem.'
				} );
			}
		}
	};

	const handleRemoveMedia = () => {
		setSelectedImage( null );
		setImagePreview( null );
		setSelectedDocument( null );
	};

	const handleDocumentUpload = async ( event ) => {
		const file = event.target.files[0];
		if ( file ) {
			const allowedTypes = [
				'application/pdf',
				'application/msword',
				'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
				'application/vnd.ms-excel',
				'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
			];

			if ( !allowedTypes.includes( file.type ) ) {
				setShowNotify( {
					status : 'error',
					text   : 'Por favor, selecione apenas arquivos do tipo PDF, Word ou Excel.'
				} );
				return;
			}

			try {
				const { mimetype, base64String } = await convertToBase64( file );
				setSelectedDocument( {
					base64   : base64String,
					fileName : file.name,
					type     : file.type,
					mimetype,
				} );
				handleCloseActionDialog();
			} catch ( error ) {
				console.error( 'Erro ao converter documento:', error );
				setShowNotify( {
					status : 'error',
					text   : 'Erro ao processar o documento.'
				} );
			}
		}
	};

	const handleSendMessageWithScroll = async ( e, useForm, userEmail ) => {
		if ( selectedImage ) {
			const messageData = {
				mediaType  : 'image',
				message    : useForm.watch( 'message' ),
				content    : selectedImage.base64String,
				mimetype   : selectedImage.mimetype,
				storageUrl : imagePreview,
				createdAt  : new Date().toISOString(),
				status     : 'pending'
			};

			handleSendMessage( messageData, useForm, userEmail );
			handleRemoveMedia();
		} else if ( selectedDocument ) {
			const messageData = {
				message   : useForm.watch( 'message' ),
				mediaType : 'document',
				fileName  : selectedDocument.fileName,
				content   : selectedDocument.base64,
				mimetype  : selectedDocument.mimetype,
				createdAt : new Date().toISOString(),
				status    : 'pending'
			};

			handleSendMessage( messageData, useForm, userEmail );
			handleRemoveMedia();
		} else {
			const messageData = {
				message   : useForm.watch( 'message' ),
				mediaType : 'text',
				createdAt : new Date().toISOString(),
				status    : 'pending'
			};
			await handleSendMessage( messageData, useForm, userEmail );
		}
		setShouldScrollToBottom( true );
	};

	const handleAssignChat = async () => {
		try {
			setLoadingAssignChat( true );
			await assignChat( selectedChat?.id, userId, userEmail, chatIdInput );
			handleOpenAssignDialog();
			setChatIdInput( '' );
		} finally {
			setLoadingAssignChat( false );
		}
	};

	const handleCloseService = async () => {
		if ( selectedChat?.userEmail !== userEmail ) {
			setShowNotify( {
				status : 'error',
				text   : 'Apenas o responsável pelo chat pode finalizá-lo.'
			} );
			return;
		}

		setLoadingCloseService( true );
		await closeChat( isCaseAssigned, selectedChat?.id );
		setLoadingCloseService( false );
	};

	useEffect( () => {
		if ( chatRef.current && ( shouldScrollToBottom || isInitialLoad.current ) ) {
			chatRef.current.scrollTop = chatRef.current.scrollHeight;
			setShouldScrollToBottom( false );
			isInitialLoad.current = false;
		}
	}, [ messages, shouldScrollToBottom ] );

	const handleKeyDown = ( e ) => {
		if ( e.key === 'Enter' && !e.shiftKey ) {
			e.preventDefault();
			useForm.handleSubmit( event => handleSendMessageWithScroll( event, useForm, selectedChat.userEmail ) )( e );
		}
	};

	const handleSetMessage = ( message ) => {
		useForm.setValue( 'message', message );
	};

	// Agrupa as mensagens por data
	const groupedMessages = groupMessagesByDate( messages );
	const messageDates = Object.keys( groupedMessages );

	const analyzeAudio = useCallback( () => {
		if ( !analyserRef.current || !dataArrayRef.current ) {
			return;
		}

		const analyser = analyserRef.current;
		const dataArray = dataArrayRef.current;

		// Usar getByteTimeDomainData para capturar amplitude real da onda sonora
		analyser.getByteTimeDomainData( dataArray );

		// Calcular RMS (Root Mean Square) para intensidade real
		let sumSquares = 0;
		let peak = 0;

		for ( let i = 0; i < dataArray.length; i++ ) {
			// Converter de 0-255 para -1 a 1
			const sample = ( dataArray[i] - 128 ) / 128;
			sumSquares += sample * sample;

			// Capturar pico de amplitude
			const amplitude = Math.abs( sample );
			if ( amplitude > peak ) {
				peak = amplitude;
			}
		}

		// Calcular RMS
		const rms = Math.sqrt( sumSquares / dataArray.length );

		// Usar uma combinação de RMS e pico para responsividade
		const intensity = rms * 0.7 + peak * 0.3;

		// Aplicar boost e normalizar
		const boostedLevel = Math.min( intensity * 15, 1 );

		// Adicionar o nível atual ao array
		setAudioLevels( ( prev ) => {
			const newLevels = [ ...prev, boostedLevel ];
			const trimmed = newLevels.slice( -50 );
			return trimmed;
		} );
	}, [] );

	useEffect( () => {
		const handleClickOutside = ( event ) => {
			if ( openActionDialog ) {
				const dialogElement = document.querySelector( '.action-dialog' );
				if ( dialogElement && !dialogElement.contains( event.target ) && 
					actionButtonRef.current && !actionButtonRef.current.contains( event.target ) ) {
					handleCloseActionDialog();
				}
			}
		};

		document.addEventListener( 'mousedown', handleClickOutside );
		return () => {
			document.removeEventListener( 'mousedown', handleClickOutside );
		};
	}, [ openActionDialog ] );

	const startAnalysisLoop = useCallback( () => {
		const loop = () => {
			if ( isPlayingRef.current ) {
				analyzeAudio();
				animationRef.current = requestAnimationFrame( loop );
			}
		};
		loop();
	}, [ isRecording, analyzeAudio ] );

	const startRecording = async () => {
		try {
			const stream = await navigator.mediaDevices.getUserMedia( { audio: true } );
	
			const mediaRecorder = new MediaRecorder( stream, {
				mimeType: 'audio/webm;codecs=opus',
			} );
	
			const audioChunks = [];
	
			// Web Audio API
			const audioContext = new ( window.AudioContext || window.webkitAudioContext )();
			streamRef.current = stream;
			if ( audioContext.state === 'suspended' ) {
				await audioContext.resume();
			}

			const source = audioContext.createMediaStreamSource( stream );
			const analyser = audioContext.createAnalyser();

			analyser.fftSize = 2048; // Maior resolução para melhor captura
			analyser.smoothingTimeConstant = 0.1; // Menos suavização para mais responsividade
			analyser.minDecibels = -100;
			analyser.maxDecibels = -30;

			const bufferLength = analyser.frequencyBinCount;
			const dataArray = new Uint8Array( bufferLength );

			source.connect( analyser );

			audioContextRef.current = audioContext;
			analyserRef.current = analyser;
			dataArrayRef.current = dataArray;

			mediaRecorderRef.current = mediaRecorder;
			chunksRef.current = [];

			mediaRecorder.ondataavailable = ( event ) => {
				if ( event.data.size > 0 ) {
					audioChunks.push( event.data );
					chunksRef.current.push( event.data );
				}
			};
	
			mediaRecorder.onstop = () => {
				if ( audioContext ) {
					audioContext.close();
				}
				cancelAnimationFrame( animationRef.current );
			};
	
			mediaRecorderRef.current = {
				recorder : mediaRecorder,
				chunks   : audioChunks,
			};
	
			mediaRecorder.start( 100 );
			setIsRecording( true );
			setRecordingTime( 0 );
			setAudioLevels( [] );
			isPlayingRef.current = true;
	
			recordingTimerRef.current = setInterval( () => {
				setRecordingTime( ( prev ) => prev + 1 );
			}, 1000 );

			setTimeout( () => {
				startAnalysisLoop();
			}, 500 );
		} catch ( error ) {
			console.error( 'Erro ao iniciar gravação:', error );
			setShowNotify( {
				status : 'error',
				text   : 'Erro ao acessar o microfone. Verifique as permissões do navegador.',
			} );
		}
	};
	

	const handleSendAudio = async () => {
		try {
			if ( mediaRecorderRef.current && isRecording ) {
				const { recorder, chunks } = mediaRecorderRef.current;

				recorder.stop();
				recorder.stream.getTracks().forEach( ( track ) => track.stop() );
				clearInterval( recordingTimerRef.current );
				setIsRecording( false );

				if ( chunks.length === 0 ) {
					throw new Error( 'Nenhum dado de áudio foi gravado' );
				}

				const audioBlob = new Blob( chunks, { type: 'audio/webm;codecs=opus' } );

				if ( audioBlob.size === 0 ) {
					throw new Error( 'Blob de áudio está vazio' );
				}

				const audioUrl = URL.createObjectURL( audioBlob );

				const base64Audio = await new Promise( ( resolve, reject ) => {
					const reader = new FileReader();
					reader.onloadend = () => {
						const base64String = reader.result.split( ',' )[1];
						resolve( base64String );
					};
					reader.onerror = ( error ) => {
						console.error( 'Erro na conversão para base64:', error );
						reject( error );
					};
					reader.readAsDataURL( audioBlob );
				} );

				const messageData = {
					mediaType  : 'audio',
					content    : base64Audio,
					storageUrl : audioUrl,
					createdAt  : new Date().toISOString(),
					status     : 'pending',
					duration   : recordingTime,
				};

				await handleSendMessage( messageData, useForm, selectedChat.userEmail );
				setRecordingTime( 0 );
				setShouldScrollToBottom( true );
			}
		} catch ( error ) {
			console.error( 'Erro ao enviar áudio:', error );
			setShowNotify( {
				status : 'error',
				text   : error.message || 'Erro ao enviar áudio.',
			} );
		}
	};

	const cancelRecording = () => {
		if ( mediaRecorderRef.current && isRecording ) {
			const { recorder } = mediaRecorderRef.current;
			recorder.stream.getTracks().forEach( ( track ) => track.stop() );
			clearInterval( recordingTimerRef.current );
			setIsRecording( false );
			setRecordingTime( 0 );
			isPlayingRef.current = false;
		}
	};

	const formatRecordingTime = ( seconds ) => {
		const minutes = Math.floor( seconds / 60 );
		const remainingSeconds = seconds % 60;
		return `${minutes}:${remainingSeconds.toString().padStart( 2, '0' )}`;
	};

	useEffect( () => {
		return () => {
			if ( recordingTimerRef.current ) {
				clearInterval( recordingTimerRef.current );
			}
			if ( mediaRecorderRef.current ) {
				mediaRecorderRef.current.recorder.stream
					.getTracks()
					.forEach( ( track ) => track.stop() );
			}
		};
	}, [] );

	const handleImageClick = async ( imgUrl ) => {
		setFullscreenImage( imgUrl );
	};

	const handleCloseImageModal = () => {
		setFullscreenImage( null );
	};

	return (
		<div style={{
			width         : '100%',
			height        : '100%',
			display       : 'flex',
			flexDirection : 'column',
			boxSizing     : 'border-box'
		}}>
			<input
				type="file"
				ref={imageInputRef}
				style={{ display: 'none' }}
				accept="image/*"
				onChange={handleImageUpload}
			/>
			<input
				type="file"
				ref={documentInputRef}
				style={{ display: 'none' }}
				accept=".pdf,.doc,.docx,.xls,.xlsx"
				onChange={handleDocumentUpload}
			/>
			<ChatHeader>
				<ChatHeaderInfo>
					{selectedChat ? (
						<>
							{selectedChat.profilePicUrl ? (
								<img
									src={selectedChat.profilePicUrl}
									alt="Contact"
									width={32}
									height={32}
									className="w-full h-full rounded-circle"
								/>
							) : (
								<AccountCircle className="w-16 h-16 text-zinc-400" />
							)}
							<div className="flex flex-col gap-2">
								<h3 className="text-white">{selectedChat.name || selectedChat?.id?.split( '@' )[0]}</h3>
								<p className="text-gray-300">{selectedChat.number}</p>

								<div className="flex flex-row gap-2">
									<p className="d-flex flex-row align-items-center text-gray-300 text-sm">Atendido por:
										<ButtonSelectOperator className='ml-2' onClick={handleOpenModal}>{selectedChat?.userEmail ?? 'Nenhum responsável'} <ArrowDropDown /></ButtonSelectOperator>
									</p>
								</div>
							</div>
						</>
					) : (
						<div className="flex flex-col gap-2">
							<h3 className="text-white">Não atribuído</h3>
						</div>
					)}
				</ChatHeaderInfo>
			</ChatHeader>
			<ChatActions
				loadingCloseService={loadingCloseService}
				isCaseAssigned={isCaseAssigned}
				isConversationEnded={isConversationEnded}
				onEndConversation={handleCloseService}
				onAssignCase={handleOpenAssignDialog}
				caseId={selectedChat?.caseId}
			/>

			<DialogComponent
				confirmation={openDialog}
				title={'Insira o id do caso'}
				subText={'Digite um id de pelo menos 7 dígitos para vincular ao chat.'}
				closeFunction={handleOpenAssignDialog}
				callback={handleAssignChat}
				loading={loadingAssignChat}
				disabled={loadingAssignChat}
			>
				<input
					className="h-10 w-full rounded-md border border-gray-200 bg-white px-3 py-2 transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-gray-400"
					placeholder={'Digite o id do caso aqui'}
					value={chatIdInput}
					onChange={( e ) => setChatIdInput( e.target.value )}
					disabled={loadingAssignChat}
				/>
			</DialogComponent>

			<ChatMessages ref={chatRef} isEnded={isConversationEnded}>
				{!isLoading && messagesCursor && (
					<LoadMoreButton type='button' onClick={handleLoadMessages}>
						Carregar Mais
					</LoadMoreButton>
				)}
				{isLoading && (
					<LoadingMessageContainer>
						<MessageTimestamp>Carregando mensagens...</MessageTimestamp>
					</LoadingMessageContainer>
				)}
				
				{/* Renderiza as mensagens agrupadas por data */}
				{messageDates.map( ( date ) => (
					<React.Fragment key={date}>
						<DateSeparator>
							{formatDate( groupedMessages[date][0].createdAt )}
						</DateSeparator>
						{groupedMessages[date].map( ( message ) => (
							<Message key={message.id} sender={message.type}>
								{message?.status === 99 && <ErrorOutline title='Erro ao enviar mensagem' className='text-red' />}
								<MessageContent sender={message.type}>
									{message.userEmail && (
										<div style={{
											fontSize     : '11px',
											color        : '#444b54',
											marginBottom : 2,
											fontWeight   : 600
										}}>
											{message.userEmail}
										</div>
									)}
									<div className='flex flex-col gap-2'>
										{message.mediaType === 'image' || message.mediaType === 'document' || message.mediaType === 'audio' || message.mediaType === 'sticker' ? (
											<MediaMessage 
												mediaType={message.mediaType}
												storageUrl={message.storageUrl}
												fileName={message.fileName}
												mimetype={message.mimetype}
												content={message.content}
												mediaContent={message.mediaContent}
												sender={message.type}
												duration={message.duration}
												onImageClick={handleImageClick}
											/>
										) : (
											<p style={{ whiteSpace: 'pre-wrap' }}>
												{formatMessageContent( message.content )}
											</p>
										)}
									</div>
									<MessageTimestamp>
										{new Date( message.createdAt ).toLocaleTimeString( [], {
											hour     : '2-digit',
											minute   : '2-digit',
											hour12   : false,
											timeZone : 'UTC'
										} )}
									</MessageTimestamp>
								</MessageContent>
							</Message>
						) )}
					</React.Fragment>
				) )}
				
				{isConversationEnded && (
					<Message key='ended-message'>
						<ChatEndedMessage>
							<p>Esta conversa foi finalizada</p>
						</ChatEndedMessage>
					</Message>
				)}
			</ChatMessages>

			<div style={{ 
				padding       : '8px 16px',
				borderTop     : '1px solid rgba(134, 150, 160, 0.15)',
				background    : '#f0f2f5',
				display       : 'flex',
				flexDirection : 'column',
				gap           : '8px',
				minHeight     : '89px',
			}}>
				{/* Preview de Mídia */}
				{( imagePreview || selectedDocument ) && (
					<div style={{
						display      : 'flex',
						alignItems   : 'center',
						gap          : '8px',
						padding      : '8px',
						background   : '#f0f2f5',
						borderRadius : '8px',
						position     : 'relative',
						maxWidth     : '100%',
						overflow     : 'hidden'
					}}>
						{imagePreview && (
							<div style={{
								width        : '100px',
								height       : '100px',
								borderRadius : '4px',
								overflow     : 'hidden',
								position     : 'relative',
								flexShrink   : 0
							}}>
								<img 
									src={imagePreview} 
									alt="Preview" 
									style={{
										width     : '100%',
										height    : '100%',
										objectFit : 'cover',
										cursor    : 'pointer'
									}}
									onClick={() => handleImageClick( imagePreview )}
								/>
							</div>
						)}
						{selectedDocument && (
							<div style={{
								display      : 'flex',
								alignItems   : 'center',
								gap          : '8px',
								background   : 'rgba(134, 150, 160, 0.15)',
								padding      : '8px 12px',
								borderRadius : '8px',
								width        : '100%',
								minWidth     : 0
							}}>
								<DescriptionIcon style={{ color: '#8696a0', flexShrink: 0 }} />
								<span style={{
									overflow     : 'hidden',
									whiteSpace   : 'nowrap',
									textOverflow : 'ellipsis',
									flex         : 1
								}}>
									{selectedDocument.fileName}
								</span>
							</div>
						)}
						<button
							onClick={handleRemoveMedia}
							style={{
								position        : 'absolute',
								top             : '4px',
								right           : '4px',
								backgroundColor : 'rgba(0, 0, 0, 0.5)',
								color           : 'white',
								border          : 'none',
								borderRadius    : '50%',
								width           : '20px',
								height          : '20px',
								display         : 'flex',
								alignItems      : 'center',
								justifyContent  : 'center',
								cursor          : 'pointer',
								fontSize        : '14px',
								padding         : '0'
							}}
						>
							×
						</button>
					</div>
				)}

				{isRecording ? (
					<div style={{
						width          : '100%',
						display        : 'flex',
						gap            : '8px',
						padding        : '8px 16px',
						background     : '#f0f2f5',
						borderRadius   : '8px',
						justifyContent : 'flex-end'
					}}>
						<button
							onClick={cancelRecording}
							style={{
								background     : 'none',
								border         : 'none',
								cursor         : 'pointer',
								padding        : '6px',
								display        : 'flex',
								alignItems     : 'center',
								justifyContent : 'center',
								color          : '#969ba2',
								gap            : '4px',
							}}
						>
							<DeleteIcon />
							<span>{formatRecordingTime( recordingTime )}</span>
						</button>
					
						<div
							style={{
								display        : 'flex',
								flexDirection  : 'row',
								alignItems     : 'center',
								borderRadius   : '50px',
								justifyContent : 'flex-end',
								gap            : '2px',
								minWidth       : '181px',
								paddingRight   : '8px',
							}}>
							{
								audioLevels?.map( ( level, i ) => (
									i % 2 === 0 && (
										<div key={i} style={{
											width          : '5px',
											height         : '50px',
											display        : 'flex',
											justifyContent : 'center',
											alignItems     : 'center',
											overflow       : 'hidden',
										}}>
											<div style={{
												width           : '4px',
												height          : `${Math.min( Math.max( level * ( barMultiplier + 4 ), 4 ), maxBarHeight )}px`,
												backgroundColor : '#969ba2',
												borderRadius    : '50px',
												transition      : 'height 200ms ease-in-out',
												transformOrigin : 'center',
											}} />
										</div>
									)
								) )
							}
						</div>
					
						<div style={{
							display       : 'flex',
							flexDirection : 'row',
							alignItems    : 'center',
							gap           : '8px',
							padding       : '8px',
							paddingRight  : '0',
						}}>
							<SendButton
								type="button"
								disabled={selectedChat.userId !== userId || isConversationEnded}
								onClick={handleSendAudio}
							>
								<Send fontSize='small' className="text-white" />
							</SendButton>
						</div>
					</div>
				) : (
					<ChatInputForm onSubmit={useForm.handleSubmit( e => handleSendMessageWithScroll( e, useForm, selectedChat.userEmail ) )}>
						<ActionButton 
							ref={actionButtonRef}
							onClick={handleOpenActionDialog}
							disabled={!isActionButtonEnabled}
							type='button'
							title={!isActionButtonEnabled ? 'Você precisa ser o responsável pelo chat e ter um caso vinculado para usar esta função' : ''}
						>
							<Add fontSize="small" />
						</ActionButton>
						<ChatInput
							ref={textareaRef}
							rows={1}
							disabled={selectedChat.userId !== userId || isConversationEnded}
							type="text"
							placeholder={selectedImage ? 'Adicione uma legenda...' : selectedDocument ? 'Adicione uma descrição...' : 'Escreva sua mensagem...'}
							value={messageValue}
							onChange={handleChange}
							onKeyDown={handleKeyDown}
							style={{ maxHeight: '150px', overflowY: 'auto' }}
						/>
						<button
							type="button"
							onClick={startRecording}
							disabled={selectedChat.userId !== userId || isConversationEnded}
							style={{
								background     : 'none',
								border         : 'none',
								cursor         : 'pointer',
								padding        : '8px',
								display        : 'flex',
								alignItems     : 'center',
								justifyContent : 'center',
								color          : '#8696a0'
							}}
						>
							<MicIcon />
						</button>
						<SendButton
							type="submit"
							disabled={selectedChat.userId !== userId || isConversationEnded}
						>
							<Send fontSize='small' className="text-white" />
						</SendButton>
					</ChatInputForm>
				)}
			</div>

			{/* Dialog de ações */}
			{openActionDialog && (
				<ActionDialog 
					className="action-dialog"
					style={{ 
						top       : `${actionDialogPosition.top}px`, 
						left      : `${actionDialogPosition.left}px`,
						transform : 'none'
					}}
				>
					<ActionList>
						<ActionItem onClick={() => sendTemplateMessage( 'interesse', handleSetMessage )}>
							<DescriptionIcon fontSize="small" style={{ marginRight: '12px' }} />
							Enviar mensagem de interesse
						</ActionItem>
						<ActionItem onClick={() => sendTemplateMessage( 'proposta', handleSetMessage )}>
							<Assignment fontSize="small" style={{ marginRight: '12px' }} />
							Enviar mensagem de proposta
						</ActionItem>
						<ActionItem onClick={() => sendTemplateMessage( 'cobrança de minuta', handleSetMessage )}>
							<AttachMoney fontSize="small" style={{ marginRight: '12px' }} />
							Enviar cobrança de minuta
						</ActionItem>
						<ActionItem onClick={() => sendTemplateMessage( 'ausência de resposta', handleSetMessage )}>
							<TimerOffIcon fontSize="small" style={{ marginRight: '12px' }} />
							Ausência de resposta
						</ActionItem>
						<ActionItem onClick={() => imageInputRef.current?.click()}>
							<ImageIcon fontSize="small" style={{ marginRight: '12px' }} />
							Enviar imagem
						</ActionItem>
						<ActionItem onClick={() => documentInputRef.current?.click()}>
							<AttachFileIcon fontSize="small" style={{ marginRight: '12px' }} />
							Enviar documentos
						</ActionItem>
					</ActionList>
				</ActionDialog>
			)}

			{/* Modal para selecionar o responsável */}
			<Modal visible={visible} onClose={handleCloseModal}>
				<div style={{ width: '500px', minWidth: '90%' }}>
					<h6>Selecione o responsável</h6>
					<SelectSearch
						variant={'outlined'}
						fullWidth
						disabled={ userPermittedToAssignChat || isConversationEnded }
						size='small'
						options={operators?.map( operator => ( { label: `${operator.name} - ${operator.email}`, value: operator.id } ) ) ?? []}
						selectedState={selectedChat.userId}
						setSelectedState={( id ) => {
							handleUpdateOperator( selectedChat.id, id, operators.find( operator => operator.id === id ).email );
						}}
						input={<BootstrapInput />}
					/>
				</div>
			</Modal>

			{/* Modal de imagem fullscreen */}
			<Modal visible={!!fullscreenImage} onClose={handleCloseImageModal}>
				{fullscreenImage && (
					<div style={{
						position       : 'fixed',
						top            : 0,
						left           : 0,
						width          : '100vw',
						height         : '100vh',
						background     : 'rgba(0,0,0,0.05)',
						display        : 'flex',
						alignItems     : 'center',
						justifyContent : 'center',
						zIndex         : 9999
					}}>
						<img
							src={fullscreenImage}
							alt="Imagem em fullscreen"
							style={{
								maxWidth     : '60vw',
								maxHeight    : '70vh',
								objectFit    : 'contain',
								borderRadius : '12px',
								boxShadow    : '0 2px 16px rgba(0,0,0,0.1)'
							}}
						/>
						<button
							onClick={handleCloseImageModal}
							style={{
								position       : 'absolute',
								top            : 64,
								right          : 24,
								color          : '#a8a5a5',
								border         : 'none',
								background     : 'rgba(0,0,0,0.0)',
								width          : 70,
								height         : 60,
								display        : 'flex',
								alignItems     : 'center',
								justifyContent : 'center',
								cursor         : 'pointer',
								fontSize       : 32,
								zIndex         : 9999
							}}
						>
							×
						</button>
					</div>
				)}
			</Modal>
		</div>
	);
};

export { ChatArea };
